import { useGoogleRecaptcha } from '@/lib/recaptcha'
import { Location, MapOptions, Step } from '@/models'
import axios, { AxiosError } from 'axios'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

const { getRecaptchaToken } = useGoogleRecaptcha()

export enum DiscountCodeError {
    notEligible,
    noDiscountCodeAvailable,
    tokenInvalid,
    invalidInput,
    other,
}

export const usePadelStore = defineStore('padel', () => {
    const postalCode = ref<string>()

    const locations = ref<Array<Location>>()
    const formattedAddress = ref<string>()
    const mapOptions = ref<MapOptions>()
    const location = ref<Location>()

    const recaptchaToken = ref<string>()
    const firstName = ref<string>()
    const lastName = ref<string>()
    const email = ref<string>()
    const phone = ref<string>()
    const licensePlate = ref<string>()

    const token = ref<string>()
    const tokenValid = ref<boolean>()
    const tokenValidating = ref<boolean>(false)

    const requestCodeError = ref<DiscountCodeError>()
    const isCodeRequested = ref<boolean>(false)

    const step = computed(() => {
        if (!locations.value) {
            return Step.postalCode
        } else if (!location.value) {
            return Step.location
        } else if (requestCodeError.value !== undefined) {
            return Step.error
        } else if (!isCodeRequested.value) {
            return Step.userData
        } else {
            return Step.confirmation
        }
    })

    const formattedRequestCodeError = computed(() => {
        switch (requestCodeError.value) {
            case DiscountCodeError.notEligible:
                return 'Helaas kom je niet (meer) in aanmerking voor een kortingscode. Klopt dit volgens jou niet? Neem dan contact op met je CUPRA Specialist.'
            case DiscountCodeError.noDiscountCodeAvailable:
                return 'Helaas zijn er op dit moment geen kortingscodes beschikbaar voor je gekozen locatie. Probeer een andere locatie, of probeer later nog eens.'
            case DiscountCodeError.invalidInput:
                return 'De ingevoerde gegevens zijn ongeldig. Check je gegevens en probeer opnieuw.'
            case DiscountCodeError.tokenInvalid:
                return 'Deze link is niet meer geldig. Neem contact op als je denkt dat dit niet klopt.'
            default:
                return 'Er ging helaas iets mis. Probeer het op een later moment nog eens.'
        }
    })

    async function retrieveLocations() {
        if (!postalCode.value || !postalCode.value.match(/\d{4}[A-Za-z]{2}/)) {
            return
        }

        postalCode.value = postalCode.value.toUpperCase()

        const { data: response } = await axios.get('/api/locations/search', {
            params: {
                postal_code: postalCode.value,
            },
        })

        formattedAddress.value = response.formatted_address
        mapOptions.value = {
            centerLat: response.center.lat,
            centerLng: response.center.lng,
        }
        locations.value = response.locations
    }

    async function validateToken(tokenValue: string) {
        token.value = tokenValue
        tokenValidating.value = true

        try {
            await axios.get(`/api/token/${tokenValue}/validate`)
            tokenValid.value = true
        } catch {
            tokenValid.value = false
        } finally {
            tokenValidating.value = false
        }
    }

    async function requestDiscountCode() {
        if (!location.value || !email.value) {
            return
        }

        if (!token.value && !licensePlate.value) {
            return
        }

        try {
            recaptchaToken.value = await getRecaptchaToken('request_discount_code')
        } catch (e: unknown) {
            console.error(e)
            requestCodeError.value = DiscountCodeError.other
            return
        }

        try {
            if (token.value) {
                await axios.post(`/api/token/${token.value}/locations/${location.value.id}/discount-code`, {
                    recaptcha_token: recaptchaToken.value,
                    first_name: firstName.value,
                    last_name: lastName.value,
                    email: email.value,
                })
            } else {
                await axios.post(`/api/locations/${location.value.id}/discount-code`, {
                    recaptcha_token: recaptchaToken.value,
                    first_name: firstName.value,
                    last_name: lastName.value,
                    email: email.value,
                    phone: phone.value,
                    license_plate: licensePlate.value,
                })
            }
            isCodeRequested.value = true
        } catch (e: unknown) {
            if (e instanceof AxiosError) {
                switch (e.status) {
                    case 409:
                        requestCodeError.value = DiscountCodeError.notEligible
                        break
                    case 404:
                        requestCodeError.value = DiscountCodeError.noDiscountCodeAvailable
                        break
                    case 410:
                        requestCodeError.value = DiscountCodeError.tokenInvalid
                        break
                    case 422:
                        requestCodeError.value = DiscountCodeError.invalidInput
                        break
                    default:
                        requestCodeError.value = DiscountCodeError.other
                        break
                }
            } else {
                requestCodeError.value = DiscountCodeError.other
            }
        }
    }

    function $reset() {
        isCodeRequested.value = false
        requestCodeError.value = undefined

        firstName.value = undefined
        lastName.value = undefined
        email.value = undefined
        phone.value = undefined
        licensePlate.value = undefined

        location.value = undefined

        mapOptions.value = undefined
        formattedAddress.value = undefined
        locations.value = undefined

        postalCode.value = undefined

        token.value = undefined
        tokenValid.value = undefined
        tokenValidating.value = false
    }

    return {
        step,
        postalCode,
        retrieveLocations,
        locations,
        formattedAddress,
        mapOptions,
        location,
        firstName,
        lastName,
        email,
        phone,
        licensePlate,
        token,
        tokenValid,
        tokenValidating,
        validateToken,
        requestDiscountCode,
        requestCodeError,
        formattedRequestCodeError,
        isCodeRequested,
        $reset,
    }
})
